/* oxlint-disable react-refresh/only-export-components --
 * Deliberately one module: the dropdown options are derived from, and mapped
 * back to, the stored alignment by the pure helpers exported here for unit
 * tests, alongside the component that renders them. The tradeoff is that
 * editing this file falls back to a full reload instead of a hot refresh. */
import {
  Autocomplete,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { EventRecord } from "@/shared/domain/events";
import { Button } from "@/shared/foundation/primitives";
import { actions, useVizDispatch } from "@/shared/state/visualization";
import { XAxisAlignment } from "@/shared/state/visualization/schema/v1";

import { useWorkspaceEvents } from "../../WorkspaceCtx/useWorkspaceEvents";

import styles from "./XAxisAlignmentConfig.module.css";

interface XAxisAlignmentConfigProps {
  panelId: string;
  xAxisAlignment: XAxisAlignment | undefined;
}

type UnitOption = "absolute" | "session" | "series";

type AnchorOption = "session-start" | "first-data" | "event";

const UnitOptionLabels: Record<UnitOption, string> = {
  absolute: "Absolute time",
  session: "Relative to session",
  series: "Relative to each series",
};

const AnchorOptionLabels: Record<AnchorOption, string> = {
  "session-start": "Session start",
  "first-data": "First data",
  event: "Event…",
};

/** The alignment unit dropdown option matching the panel's applied alignment. */
export function appliedUnitOption(
  xAxisAlignment: XAxisAlignment | undefined,
): UnitOption {
  return xAxisAlignment?.unit ?? "absolute";
}

/**
 * The "Zero at" dropdown option matching the applied alignment, or undefined
 * for an alignment with no session anchor (absolute time or per-series).
 */
export function appliedAnchorOption(
  xAxisAlignment: XAxisAlignment | undefined,
): AnchorOption | undefined {
  if (xAxisAlignment?.unit !== "session") {
    return undefined;
  }
  if (typeof xAxisAlignment.anchor === "object") {
    return "event";
  }
  return xAxisAlignment.anchor;
}

/** The event name of an applied event anchor, or undefined for any other alignment. */
export function appliedEventName(
  xAxisAlignment: XAxisAlignment | undefined,
): string | undefined {
  if (
    xAxisAlignment?.unit === "session" &&
    typeof xAxisAlignment.anchor === "object"
  ) {
    return xAxisAlignment.anchor.eventName;
  }
  return undefined;
}

/**
 * The alignment to dispatch when the alignment unit dropdown changes to `option`.
 * Switching to the session unit starts at the session-start anchor, keeping
 * the applied anchor when the panel is already session-scoped.
 */
export function alignmentForUnitOption(
  option: UnitOption,
  applied: XAxisAlignment | undefined,
): XAxisAlignment | undefined {
  switch (option) {
    case "absolute":
      return undefined;
    case "series":
      return { unit: "series" };
    case "session":
      if (applied?.unit === "session") {
        return applied;
      }
      return { unit: "session", anchor: "session-start" };
  }
}

/**
 * The event names offered by the anchor autocomplete: trimmed, deduplicated,
 * sorted, and without blank names, which the Apply button rejects.
 *
 * Trimming comes before deduplicating because the applied name is trimmed too:
 * offering "takeoff" and "takeoff " as separate options would give the user two
 * choices that anchor the panel identically.
 */
export function distinctEventNames(events: EventRecord[]): string[] {
  return [...new Set(events.map((event) => event.name.trim()))]
    .filter((name) => name.length > 0)
    .sort();
}

/**
 * Chooses how the panel positions its series on the x-axis, composed inside
 * the "X-Axis" row of PlotPanelSettings: an alignment unit select (absolute
 * time, relative to session, relative to each series) and, when the unit is
 * session-scoped, a "Zero at" anchor select.
 *
 * Both selects apply on change, except the "Event…" anchor option, which
 * applies only when its name field is submitted: every applied name
 * re-resolves the alignment and sends it to the plot worker, which rebuilds
 * the chart, so applying on change would rebuild it on every keystroke. The
 * field resynchronizes whenever the applied alignment changes outside this
 * form, for example when a saved layout loads.
 */
export function XAxisAlignmentConfig({
  panelId,
  xAxisAlignment,
}: XAxisAlignmentConfigProps) {
  const dispatch = useVizDispatch();

  const appliedUnit = appliedUnitOption(xAxisAlignment);
  const appliedAnchor = appliedAnchorOption(xAxisAlignment);
  const currentEventName = appliedEventName(xAxisAlignment);

  // "Event…" is selectable before any name is applied, so the pending choice
  // lives here until the name is submitted.
  const [pendingEvent, setPendingEvent] = React.useState(false);
  const [eventName, setEventName] = React.useState(currentEventName ?? "");

  // An applied event anchor supersedes the pending choice. Left set, it would
  // keep the event form showing after the anchor later changes to
  // session-start or first-data outside this form.
  React.useEffect(() => {
    if (appliedAnchor === "event") {
      setPendingEvent(false);
    }
  }, [appliedAnchor]);

  React.useEffect(() => {
    setEventName(currentEventName ?? "");
  }, [currentEventName]);

  const onUnitChange = (event: SelectChangeEvent) => {
    const option = event.target.value as UnitOption;
    setPendingEvent(false);
    dispatch(
      actions.setPlotPanelConfig(panelId, {
        xAxisAlignment: alignmentForUnitOption(option, xAxisAlignment),
      }),
    );
  };

  const selectedAnchor = pendingEvent ? "event" : appliedAnchor;

  const onAnchorChange = (event: SelectChangeEvent) => {
    const option = event.target.value as AnchorOption;
    if (option === "event") {
      setPendingEvent(true);
      return;
    }
    setPendingEvent(false);
    dispatch(
      actions.setPlotPanelConfig(panelId, {
        xAxisAlignment: { unit: "session", anchor: option },
      }),
    );
  };

  const canApplyEventName =
    eventName.trim().length > 0 && eventName.trim() !== currentEventName;

  const onApplyEventName = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canApplyEventName) {
      return;
    }
    setPendingEvent(false);
    dispatch(
      actions.setPlotPanelConfig(panelId, {
        xAxisAlignment: {
          unit: "session",
          anchor: { eventName: eventName.trim() },
        },
      }),
    );
  };

  return (
    <>
      <FormControl className={styles.control} fullWidth>
        <Select
          id="x-axis-alignment-unit-select"
          value={appliedUnit}
          onChange={onUnitChange}
          size="small"
          className={styles.smallFont}
          inputProps={{ "aria-label": "X-axis alignment" }}
        >
          {Object.entries(UnitOptionLabels).map(([value, label]) => (
            <MenuItem key={value} value={value} className={styles.smallFont}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <SessionAnchorControl
        visible={appliedUnit === "session"}
        selectedAnchor={selectedAnchor}
        onAnchorChange={onAnchorChange}
        eventName={eventName}
        onEventNameChange={setEventName}
        canApply={canApplyEventName}
        onApply={onApplyEventName}
      />
    </>
  );
}

interface SessionAnchorControlProps {
  visible: boolean;
  selectedAnchor: AnchorOption | undefined;
  onAnchorChange: (event: SelectChangeEvent) => void;
  eventName: string;
  onEventNameChange: (eventName: string) => void;
  canApply: boolean;
  onApply: (event: React.FormEvent) => void;
}

function SessionAnchorControl({
  visible,
  selectedAnchor,
  onAnchorChange,
  eventName,
  onEventNameChange,
  canApply,
  onApply,
}: SessionAnchorControlProps) {
  if (!visible) {
    return null;
  }

  return (
    <div className={styles.anchorBlock}>
      <RobotoTypography className={styles.subLabel}>Zero at</RobotoTypography>
      <FormControl className={styles.control} fullWidth>
        <Select
          id="x-axis-alignment-anchor-select"
          value={selectedAnchor ?? ""}
          onChange={onAnchorChange}
          size="small"
          className={styles.smallFont}
          inputProps={{ "aria-label": "X-axis alignment zero point" }}
        >
          {Object.entries(AnchorOptionLabels).map(([value, label]) => (
            <MenuItem key={value} value={value} className={styles.smallFont}>
              {label}
            </MenuItem>
          ))}
        </Select>
        <AlignmentEventNameForm
          visible={selectedAnchor === "event"}
          eventName={eventName}
          onEventNameChange={onEventNameChange}
          canApply={canApply}
          onApply={onApply}
        />
      </FormControl>
    </div>
  );
}

interface AlignmentEventNameFormProps {
  visible: boolean;
  eventName: string;
  onEventNameChange: (eventName: string) => void;
  canApply: boolean;
  onApply: (event: React.FormEvent) => void;
}

function AlignmentEventNameForm({
  visible,
  eventName,
  onEventNameChange,
  canApply,
  onApply,
}: AlignmentEventNameFormProps) {
  // Called even while the form is hidden, since a hook cannot be conditional.
  // Deduplicated by query key with the workspace's other useWorkspaceEvents
  // callers, so asking for the events here adds no fetch.
  const eventsQuery = useWorkspaceEvents();

  if (!visible) {
    return null;
  }

  const options = distinctEventNames(eventsQuery.data ?? []);

  return (
    <form className={styles.eventNameForm} onSubmit={onApply}>
      <Autocomplete
        className={styles.eventNameField}
        freeSolo
        size="small"
        options={options}
        loading={eventsQuery.isLoading}
        inputValue={eventName}
        onInputChange={(_event, newInputValue) =>
          onEventNameChange(newInputValue)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Event name"
            inputProps={{
              ...params.inputProps,
              "aria-label": "Alignment event name",
            }}
          />
        )}
      />
      <Button type="submit" size="small" disabled={!canApply}>
        Apply
      </Button>
    </form>
  );
}
